import React from 'react';
import { Bell, BellOff, ShieldCheck, AlertTriangle, Users, Zap, Calendar, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

/**
 * Per-tenant recipient config for Web Push notifications.
 *
 * The parent (`SettingsPage`) owns `formData.push_enabled`,
 * `formData.push_notify_roles`, `formData.push_notify_events`; this card
 * just renders switches for each role/event. Nothing about the browser
 * subscription happens here — that's handled by `PushToggleButton` per user
 * per device. This is the *policy* layer (who gets what event).
 */

const ROLE_OPTIONS = [
  { key: 'company_admin',       label: 'Administradores',           hint: 'Utilizadores com role `company_admin`' },
  { key: 'compliance_officer',  label: 'Compliance Officers',       hint: 'Utilizadores com role `compliance_officer`' },
  { key: 'triage_manager',      label: 'Gestores de Triagem',       hint: 'Locais e globais (CogniSeg / LP)' },
  { key: 'dpo',                 label: 'DPO',                       hint: 'Responsável pela proteção de dados' },
  { key: 'case_manager',        label: 'Gestores de Casos',         hint: 'Utilizadores com role `case_manager` (opt-in)' },
  { key: 'investigator',        label: 'Investigadores',            hint: 'Utilizadores com role `investigator` (opt-in)' },
];

const EVENT_OPTIONS = [
  { key: 'NEW_REPORT',     label: 'Nova denúncia',          icon: FileText,      hint: 'Denúncia recebida no canal' },
  { key: 'ASSIGNMENT',     label: 'Atribuição de caso',     icon: Users,         hint: 'Um caso foi-lhe atribuído' },
  { key: 'CONFLICT_ALERT', label: 'Conflito de interesses', icon: AlertTriangle, hint: 'Detetado por regra ou IA' },
  { key: 'DEADLINE_ALERT', label: 'Prazos legais',          icon: Calendar,      hint: 'D+7 acuse, D+90 feedback (Lei 93/2021)' },
  { key: 'CRITICAL',       label: 'Alertas críticos',       icon: Zap,           hint: 'Severidade CRITICO' },
];

const toggleInArray = (arr, key) =>
  arr.includes(key) ? arr.filter(v => v !== key) : [...arr, key];

export const PushNotificationsCard = ({ formData, setFormData }) => {
  const enabled  = formData.push_enabled !== false;
  const roles    = Array.isArray(formData.push_notify_roles)  ? formData.push_notify_roles  : [];
  const events   = Array.isArray(formData.push_notify_events) ? formData.push_notify_events : [];

  return (
    <Card className="bg-white border-slate-200" data-testid="push-notifications-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              {enabled ? <Bell className="w-5 h-5 text-indigo-700" /> : <BellOff className="w-5 h-5 text-slate-400" />}
              Notificações Push (Browser)
            </CardTitle>
            <CardDescription className="text-slate-500 max-w-2xl">
              Escolha quais perfis recebem alertas em tempo real no browser (via Web Push).
              Cada utilizador tem ainda de ativar as notificações neste dispositivo — este
              painel apenas define quem <em>pode</em> receber.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Label htmlFor="push-enabled-switch" className="text-sm text-slate-700">
              {enabled ? 'Ativas' : 'Desativas'}
            </Label>
            <Switch
              id="push-enabled-switch"
              checked={enabled}
              onCheckedChange={(v) => setFormData(prev => ({ ...prev, push_enabled: v }))}
              data-testid="push-enabled-switch"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className={`space-y-6 ${enabled ? '' : 'opacity-60 pointer-events-none'}`}>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-slate-600" />
            <h3 className="font-medium text-slate-900">Perfis que recebem push</h3>
            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">
              {roles.length} / {ROLE_OPTIONS.length}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ROLE_OPTIONS.map(opt => {
              const on = roles.includes(opt.key);
              return (
                <label
                  key={opt.key}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    on ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                  data-testid={`push-role-toggle-${opt.key}`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      push_notify_roles: toggleInArray(roles, opt.key),
                    }))}
                    className="mt-1 accent-indigo-600"
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500 truncate">{opt.hint}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-slate-600" />
            <h3 className="font-medium text-slate-900">Eventos que disparam push</h3>
            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">
              {events.length} / {EVENT_OPTIONS.length}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {EVENT_OPTIONS.map(opt => {
              const on = events.includes(opt.key);
              const Icon = opt.icon;
              return (
                <label
                  key={opt.key}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    on ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                  data-testid={`push-event-toggle-${opt.key}`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      push_notify_events: toggleInArray(events, opt.key),
                    }))}
                    className="mt-1 accent-indigo-600"
                  />
                  <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500 truncate">{opt.hint}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <Calendar className="w-4 h-4 text-slate-600" />
                Resumo diário (07h UTC)
              </div>
              <p className="text-xs text-slate-500 mt-1">
                1 push por dia com <span className="font-medium">pendentes</span>,
                <span className="font-medium"> prazos vencidos</span> e
                <span className="font-medium"> denúncias críticas nas últimas 24h</span>.
                Independente dos eventos acima — pode desligar os eventos e manter só o resumo.
              </p>
            </div>
            <Switch
              checked={formData.push_digest_enabled !== false}
              onCheckedChange={(v) => setFormData(prev => ({ ...prev, push_digest_enabled: v }))}
              data-testid="push-digest-switch"
            />
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600">
          <p className="font-medium text-slate-700 mb-1">Notas</p>
          <ul className="list-disc list-inside space-y-1">
            <li>O gestor global CogniSeg recebe sempre alertas críticos, independentemente do que estiver aqui configurado.</li>
            <li>Cada utilizador tem de ativar as notificações no seu browser (botão no topo do dashboard).</li>
            <li>Guarde as definições no fim da página para aplicar as alterações.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
